import { useToken } from "@chakra-ui/react";
import { setColorFunction } from "@geomatico/maplibre-cog-protocol";
import { FeatureCollection, GeoJsonGeometryTypes, Polygon } from "geojson";
import { useEffect, useMemo } from "react";
import { Layer, LayerProps, Source, SourceProps } from "react-map-gl/maplibre";

import { removeNullUndefinedObj } from "@nens/shared-utils";

import { VECTOR_LAYER_ID } from "@/config/map-layer-constants";
import { useInverseMask } from "@/hooks/use-inverse-mask";
import { RasterColorStop, interpolateRgbByStops, toRgbStops } from "@/utils/raster-color-stops";

import { BUILDING_LAYER_ID } from "../consts/building-layer-ids";

// This looks like (and is) a complicated bit of typescript code but what it simply does is that it looks through the different types of SourceProps.
// If the props contain bounds. Instead of making the bounds of type {bounds?: Extent} make them of type {bounds: Extent | undefined | null}
// This makes sure that we do send the bounds so we don't get and render unneeded parts of a raster for example
// But also allows us to simply send null to it because that is what we get from the backend.
// It looks so complicated because maplibre is very strict in its typing and wants us to keep track of which type is in the source props

type NullableBounds<T> = T extends { bounds?: infer B }
  ? Omit<T, "bounds"> & { bounds: B | undefined | null }
  : T;

type SourceTyped = SourceProps["type"];
type SourcePropsTyped<T extends SourceTyped> = Extract<SourceProps, { type: T }>;

type RanaMapSourceProps<T extends SourceTyped> = NullableBounds<SourcePropsTyped<T>>;

// You need to remove bounds if it is null or undefined because maplibre doesn't like undefined bounds
const removeEmptyBounds = <T extends SourceTyped>(props: RanaMapSourceProps<T>) => {
  if ("bounds" in props && !props.bounds) {
    const { bounds, ...rest } = props;
    return rest;
  }
  return props;
};

const RanaMapSource = <T extends SourceTyped = SourceTyped>(props: RanaMapSourceProps<T>) => {
  return (
    <Source
      key={"bounds" in props && props.bounds ? props.bounds.toString() : undefined}
      {...removeEmptyBounds(props)}
    />
  );
};

type ExtraLayerProps = {
  isVisible?: boolean;
};

type LayerType = LayerProps["type"];
type LayerPropsTyped<T extends LayerType> = Extract<LayerProps, { type: T }> & { id: string }; // Making id required makes it work more consistent. Especially when rendering a cog layer the visbility in layers layout does not always work.

const usePaint = <T extends LayerType>(type: T) => {
  const [defaultColor, borderColor] = useToken("colors", ["map.default", "map.border"]);

  const paintMap: {
    [key in LayerType]?: "paint" extends keyof LayerPropsTyped<key>
      ? LayerPropsTyped<key>["paint"]
      : null;
  } = {
    fill: {
      "fill-color": defaultColor,
      "fill-opacity": 0.2,
    },
    line: {
      "line-color": borderColor,
      "line-width": 0.5,
      "line-opacity": 0.8,
    },
    circle: {
      "circle-color": borderColor,
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 1, 10, 3, 15, 6],
      "circle-opacity": 0.8,
    },
  };

  return paintMap[type];
};

type RanaMapLayerProps<T extends LayerType> = LayerPropsTyped<T> & ExtraLayerProps;

const RanaMapLayer = <T extends LayerType = LayerType>({
  isVisible = true,
  beforeId,
  ...props
}: RanaMapLayerProps<T>) => {
  const paint = usePaint(props.type);

  return (
    <Layer
      {...props}
      beforeId={beforeId ?? BUILDING_LAYER_ID}
      paint={removeNullUndefinedObj({
        ...paint,
        ...("paint" in props ? props.paint : {}),
      } as NonNullable<typeof paint>)}
      layout={removeNullUndefinedObj({
        visibility: isVisible ? "visible" : "none",
        ...("layout" in props ? props.layout : {}),
      })}
    />
  );
};

export const getPmTileLayerType = (type: GeoJsonGeometryTypes | undefined) => {
  const typeMap: Record<GeoJsonGeometryTypes, "fill" | "line" | "circle" | "all"> = {
    Point: "circle",
    MultiPoint: "circle",
    LineString: "line",
    MultiLineString: "line",
    Polygon: "fill",
    MultiPolygon: "fill",
    GeometryCollection: "all",
  };

  return type ? typeMap[type] : "all";
};

export interface RanaMapPmTileLayerProps extends ExtraLayerProps {
  id?: string;
  "source-layer": string;
  type: GeoJsonGeometryTypes | undefined;
  beforeId?: string;
  fillProps?: Omit<LayerPropsTyped<"fill">, "type">;
  lineProps?: Omit<LayerPropsTyped<"line">, "type">;
  circleProps?: Omit<LayerPropsTyped<"circle">, "type">;
}

const RanaMapPmTileLayer = ({
  id,
  type,
  beforeId,
  fillProps,
  lineProps,
  circleProps,
  ...props
}: RanaMapPmTileLayerProps) => {
  const pmTileType = getPmTileLayerType(type);
  const layerTypes: LayerProps["type"][] =
    pmTileType === "all" ? ["circle", "line", "fill"] : [pmTileType];

  return layerTypes.map((layerType) => {
    const passProps = {
      type: layerType,
      id: id,
      ...(layerType === "fill" ? fillProps : {}),
      ...(layerType === "circle" ? circleProps : {}),
      ...(layerType === "line" ? lineProps : {}),
      ...props,
    } as RanaMapLayerProps<typeof layerType>;

    return <RanaMapLayer beforeId={beforeId} key={layerType} {...passProps} />;
  });
};

type ExtentBounds = [number, number, number, number];

// ExtentLayer derives its source and layer ids from its `id`. Use this to reference them elsewhere,
// for example in `interactiveLayerIds`.
export const getExtentLayerIds = (id = "extent") => ({
  sourceId: `${id}-source`,
  fillId: `${id}-fill`,
  lineId: `${id}-line`,
});

// Turns one or many extents into a single FeatureCollection with one polygon per extent.
export const getExtentsFeatureCollection = (
  bounds: ExtentBounds | ExtentBounds[] | null | undefined
): FeatureCollection<Polygon> | null => {
  if (!bounds || bounds.length === 0) return null;

  const boundsList =
    typeof bounds[0] === "number" ? [bounds as ExtentBounds] : (bounds as ExtentBounds[]);

  return {
    type: "FeatureCollection",
    features: boundsList.map(([minLng, minLat, maxLng, maxLat]) => ({
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [minLng, minLat],
            [maxLng, minLat],
            [maxLng, maxLat],
            [minLng, maxLat],
            [minLng, minLat],
          ],
        ],
      },
      properties: {},
    })),
  };
};

interface ExtentLayerProps extends ExtraLayerProps {
  id?: string;
  // A single extent, or a list of extents that are rendered through one shared source and layer pair.
  bounds?: ExtentBounds | ExtentBounds[] | null;
  beforeId?: string;
}

const RanaMapExtentLayer = ({ id = "extent", bounds, ...rest }: ExtentLayerProps) => {
  const extentsGeoJson = useMemo(() => getExtentsFeatureCollection(bounds), [bounds]);

  if (!extentsGeoJson) return null;

  const { sourceId, fillId, lineId } = getExtentLayerIds(id);

  return (
    <>
      <Source id={sourceId} type="geojson" data={extentsGeoJson} />
      <RanaMapLayer
        id={fillId}
        type="fill"
        source={sourceId}
        paint={{
          "fill-color": "blue",
          "fill-opacity": 0.2,
        }}
        {...rest}
      />
      <RanaMapLayer
        id={lineId}
        type="line"
        source={sourceId}
        paint={{
          "line-color": "blue",
          "line-width": 4,
        }}
        {...rest}
      />
    </>
  );
};

interface RanaMapVectorLayerProps extends ExtraLayerProps {
  isInverse: boolean;
  layer: string;
  url: string;
  type: GeoJsonGeometryTypes | undefined;
  styleLayers?: LayerProps[];
  bounds?: [number, number, number, number] | null;
}

const RanaMapVectorLayer = ({
  isInverse,
  layer,
  url,
  type,
  styleLayers,
  bounds,
  isVisible = true,
}: RanaMapVectorLayerProps) => {
  const isPolygon = type === "Polygon" || type === "MultiPolygon";
  const fillStyleLayers = styleLayers?.filter((l) => l.type === "fill") ?? [];
  const nonFillStyleLayers = styleLayers?.filter((l) => l.type !== "fill") ?? [];
  const usesInverseMask = isInverse && isPolygon && fillStyleLayers.length > 0;
  const activeStyleLayers = usesInverseMask ? nonFillStyleLayers : styleLayers;

  const sourceId = `layer-source-${layer}-${url}`;
  const maskGeoJson = useInverseMask({ isActive: usesInverseMask, sourceId });

  return (
    <>
      <RanaMapSource id={sourceId} bounds={bounds} type="vector" url={`pmtiles://${url}`}>
        {activeStyleLayers && activeStyleLayers.length > 0 ? (
          activeStyleLayers.map((styleLayer) => (
            <RanaMapLayer
              key={styleLayer.id + layer}
              id={styleLayer.id + layer}
              isVisible={isVisible}
              {...styleLayer}
            />
          ))
        ) : (
          <RanaMapPmTileLayer
            id={layer}
            source-layer={VECTOR_LAYER_ID}
            type={type}
            isVisible={isVisible}
          />
        )}
      </RanaMapSource>
      {usesInverseMask && maskGeoJson && (
        <Source id={`layer-mask-source-${layer}-${url}`} type="geojson" data={maskGeoJson}>
          <RanaMapLayer
            id={`layer-mask-fill-${layer}-${url}`}
            type="fill"
            paint={{
              "fill-color": "#000000",
              "fill-opacity": 0.3,
              ...fillStyleLayers[0].paint,
            }}
            isVisible={isVisible}
          />
        </Source>
      )}
    </>
  );
};

export type RanaCogRasterLayer = {
  id: string;
  isHillshade: boolean;
  name: string;
  url: string;
  colorRange: [number, number] | undefined;
  defaultColorRange: [number, number] | undefined;
  colorStops: RasterColorStop[];
};

function withCogStyleKey(url: string, id: string, colorStops?: RasterColorStop[]) {
  // setColorFunction is keyed by full COG URL string. Use a deterministic query key
  // so custom styling stays scoped to this visual layer and doesn't affect hillshade.
  // Also add the color stops to the key so that if the color stops change we get a new
  // key and thus a new color function is set. This is important because otherwise the
  //  color function would not be updated when the color stops change.
  let key = id;

  if (colorStops?.length) {
    const serialized = colorStops
      .map((stop) => `${stop.value.toFixed(6)}:${stop.color.toLowerCase()}`)
      .join("|");

    let hash = 5381;
    for (let i = 0; i < serialized.length; i++) {
      hash = (hash * 33) ^ serialized.charCodeAt(i);
    }

    key = `${id}:${(hash >>> 0).toString(36)}`;
  }

  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}cogStyleKey=${encodeURIComponent(key)}`;
}

interface RanaMapCogLayerProps extends RanaCogRasterLayer, ExtraLayerProps {
  bounds: [number, number, number, number] | null;
  beforeId?: string;
}

const RanaMapCogLayer = ({
  id,
  url,
  isHillshade,
  colorStops,
  isVisible,
  bounds,
  beforeId,
}: RanaMapCogLayerProps) => {
  const shouldUseCustomStyle = !isHillshade && (colorStops.length ?? 0) > 1;
  const styledUrl = shouldUseCustomStyle && url ? withCogStyleKey(url, id, colorStops) : url;

  useEffect(() => {
    if (!styledUrl || !shouldUseCustomStyle || !colorStops?.length) return;

    const sourceStops = toRgbStops(colorStops);

    if (!sourceStops.length) return;

    setColorFunction(styledUrl, (pixel, color, metadata) => {
      const rawValue = pixel[0];
      if (!Number.isFinite(rawValue) || rawValue === metadata.noData) {
        color[3] = 0;
        return;
      }

      const scale = Number.isFinite(metadata.scale) ? metadata.scale : 1;
      const offset = Number.isFinite(metadata.offset) ? metadata.offset : 0;
      const value = rawValue * scale + offset;
      const [r, g, b] = interpolateRgbByStops(value, sourceStops);

      color[0] = r;
      color[1] = g;
      color[2] = b;
      color[3] = 255;
    });

    return () => {
      setColorFunction(styledUrl, undefined);
    };
  }, [styledUrl, shouldUseCustomStyle, colorStops, id, isHillshade]);

  if (!url) return null;

  if (isHillshade) {
    return (
      <RanaMapSource
        key={`${id}-hillshade`}
        bounds={bounds}
        type="raster-dem"
        url={`cog://${url}#dem`}
        tileSize={256}
      >
        <RanaMapLayer
          beforeId={beforeId}
          id={`schematisation-raster-layer-${id}-hillshade`}
          type="hillshade"
          isVisible={isVisible}
          paint={{
            "hillshade-exaggeration": 1,
          }}
        />
      </RanaMapSource>
    );
  }

  if (shouldUseCustomStyle) {
    return (
      <RanaMapSource
        key={id}
        bounds={bounds}
        type="raster"
        url={`cog://${styledUrl}`}
        tileSize={256}
      >
        <RanaMapLayer
          beforeId={beforeId}
          id={`schematisation-raster-layer-${id}`}
          type="raster"
          isVisible={isVisible}
        />
      </RanaMapSource>
    );
  }

  // If we don't have a custom style we don't render anything for now because a style should be set
  console.warn("Could not find custom style stops for COG raster, using default color range");
  return null;
};

// This is not a real default export, the elements of the object function as named exports.
// eslint-disable-next-line import/no-default-export
export default {
  Source: RanaMapSource,
  Layer: RanaMapLayer,
  PmTileLayer: RanaMapPmTileLayer,
  ExtentLayer: RanaMapExtentLayer,
  VectorLayer: RanaMapVectorLayer,
  CogLayer: RanaMapCogLayer,
} as const;
